import { Component, OnInit } from '@angular/core';
import {
  AuthService,
  ValidationStatus,
} from '../../shared/services/AuthService';
import { ActivatedRoute, Router } from '@angular/router';
import { HelperService } from 'src/app/shared/services/Helper.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { environment } from 'src/environments/environment';
import { RestApiService } from 'src/app/shared/services/RestApiServices.service';
import { NotificationService } from 'src/app/shared/modules/notification/notification.service';
import { EventService } from 'src/app/shared/services/event.service';
import { EventWorkerService } from 'src/app/shared/services/worker/event-worker/event-worker.service';
import { TranslateService } from '@ngx-translate/core';
import { sleep } from 'src/app/shared/time.functions';
import { lastValueFrom } from 'rxjs';
import { PasswordMaxLength } from 'src/app/shared/staticObjects';
import { CompanyType } from 'src/app/shared/models/Enums';
import { ToastService } from 'src/app/shared/modules/toast/toast.service';
import { RealtimeService } from 'src/app/shared/services/realtime.service';
import { LauncherUpdateGuardService } from 'src/app/shared/services/launcher-update-guard.service';

// code=218368&email=ba5f39f0-6dba-492b-bf8b-c4c6e617777f
// mohitzadeh@gmail.com

export enum AuthPageMode {
  Login = 1,
  ForgotPassword = 2,
  ResetPassword = 3,
}

@Component({
  selector: 'auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit {
  readonly PasswordMaxLength = PasswordMaxLength;

  loader: boolean;
  showLoginPage: boolean;
  showError: boolean;
  showErrorResetPassword: boolean;
  authPageMode: AuthPageMode;
  showCompletePassword = false;
  passwordVisible: boolean;
  isAuth: boolean;
  showResetPasswordContent: boolean;

  isPasswordLawerCase: boolean;
  isPasswordUpperCase: boolean;
  isPasswordHasNumber: boolean;
  isPasswordHasSpecialCharacter: boolean;
  isPasswordUpTo8Characters: boolean;
  isMatchPasswords: boolean;

  email = new FormControl('', [Validators.required, Validators.email]);
  password = new FormControl('', [
    Validators.required,
    Validators.maxLength(PasswordMaxLength),
  ]);
  loginForm: FormGroup;

  emailF = new FormControl('', [Validators.required, Validators.email]);
  forgotPasswordForm: FormGroup;

  codeR = new FormControl('', []);
  newPasswordR = new FormControl('', [
    Validators.required,
    Validators.maxLength(PasswordMaxLength),
  ]);
  againPasswordR = new FormControl('', [
    Validators.required,
    Validators.maxLength(PasswordMaxLength),
  ]);
  passwordResetForm: FormGroup;

  constructor(
    private builder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public helperService: HelperService,
    private restApiService: RestApiService,
    private notificationService: NotificationService,
    private eventService: EventService,
    private eventWorkerService: EventWorkerService,
    private translate: TranslateService,
    private toastService: ToastService,
    private realtimeService: RealtimeService,
    private launcherUpdateGuard: LauncherUpdateGuardService,
  ) {
    this.loader =
      this.showError =
      this.isPasswordHasNumber =
      this.isPasswordHasSpecialCharacter =
      this.isPasswordLawerCase =
      this.isPasswordUpperCase =
      this.isPasswordUpTo8Characters =
      this.isMatchPasswords =
      this.showErrorResetPassword =
        false;
    this.passwordVisible = true;
    this.showLoginPage = this.isAuth = false;
    this.createForms();
    this.changeMode(AuthPageMode.Login);
    this.showResetPasswordContent = false;
  }

  createForms(): void {
    this.loginForm = this.builder.group({
      email: this.email,
      password: this.password,
    });
    this.forgotPasswordForm = this.builder.group({
      emailF: this.emailF,
    });
    this.passwordResetForm = this.builder.group({
      codeR: this.codeR,
      newPasswordR: this.newPasswordR,
      againPasswordR: this.againPasswordR,
    });
  }

  // @HostListener('window:popstate', ['$event'])
  // onPopState(event: Event) {
  //   if (!this.isAuth) {
  //     this.authService.setValidationResult = ValidationStatus.Invalid;
  //   }
  // }

  // ngAfterViewInit(): void {
  //   this.authService.isAuthenticated().then((isSignedIn) => {
  //     if (isSignedIn) {
  //       this.router.navigate([environment.defaultPage]).then(() => {
  //         this.authService.setValidationResult = ValidationStatus.Valid;
  //       });
  //     } else {
  //       this.authService.setValidationResult = ValidationStatus.Auth;
  //     }
  //   });
  // }

  async ngOnInit(): Promise<void> {
    try {
      if (this.authService.validationResult === ValidationStatus.Valid) {
        this.router.navigate([environment.defaultPage]);
      } else {
        this.showLoginPage = true;

        this.route.queryParams.subscribe((param) => {
          let email: string = param['email'] ?? '',
            code: string = param['code'] ?? '';

          if (email.indexOf(' ') > -1) {
            email = email.replace(/ /g, '+');
          }

          if (email?.length && code?.length) {
            this.emailF.setValue(email);
            this.codeR.setValue(code);
            this.authPageMode = AuthPageMode.ResetPassword;
            this.showResetPasswordContent = true;
          }
        });
      }
    } catch (err) {
      console.log('Is authenticate invalid', err);
      await this.authService.unautorized();
      return err;
    }
  }

  async onLogin(): Promise<void> {
    this.loader = true;

    let userName = this.loginForm.value.email.toLowerCase(); //'Mohitzadeh@gmail.com';
    let password = this.loginForm.value.password; //'Aa12345!';

    try {
      let res = await this.authService.signIn(userName, password);
      this.loader = false;

      if (res == 1) {
        const isAuth = await this.authService.isAuthenticatedCognitoGroups();

        if (!isAuth) {
          this.toastService.error(this.translate.instant('userNotBMAdmin'));
          await sleep(1500);
          await this.authService.signOut();
          return;
        }
      }

      if (res == 1) {
        let userId = await this.authService.getUserId();

        if (!userId?.length) {
          this.toastService.error(this.translate.instant('loginError'));
          await this.authService.unautorized();
          return;
        }

        const user = await lastValueFrom(
          this.restApiService.getUserByUserId(userId),
        );

        if (user?.company?.companyType !== CompanyType.Commercial) {
          this.toastService.error(this.translate.instant('userNotCommercial'));
          await this.authService.unautorized();
          return;
        }

        if (
          !user ||
          !user.company?.isBayManager ||
          !user.location?.isBayManager ||
          !user.baymanagerAdmin
        ) {
          if (!user) {
            this.toastService.error(this.translate.instant('loginError'));
          } else if (!user.company?.isBayManager) {
            this.toastService.error(this.translate.instant('companyNotBM'));
          } else if (!user.location?.isBayManager) {
            this.toastService.error(this.translate.instant('usersLocNotBM'));
          } else if (!user.baymanagerAdmin) {
            this.toastService.error(this.translate.instant('userNotBM'));
          }

          await this.authService.unautorized();
          return;
        }

        this.restApiService.user = user;

        this.restApiService.initialDBObjects();
        this.notificationService.requestAndShowPermission();
        this.launcherUpdateGuard.beginCheck(user.company.id);
        await this.initialEvents(user.company.id);

        this.authService.validationResult = ValidationStatus.Valid;

        this.realtimeService.connect(user.company.id);
        void this.launcherUpdateGuard.start(user.company.id);

        console.log('auth: ', res);

        this.router.navigate([environment.defaultPage]);
      } else if (res == 0 || res == -2) {
        this.showError = true;
        this.toastService.error(this.translate.instant('loginError'));
        console.log('auth failed: ', res);
        await this.authService.unautorized();
      } else if (res == -1) {
        this.changeMode(AuthPageMode.ResetPassword);

        this.showResetPasswordContent = true;
        this.showCompletePassword = true;
      }
    } catch (err) {
      console.log('Current user info failed to fetch', err);
      this.toastService.error(this.translate.instant('serverError'));
      await this.authService.unautorized();
      return err;
    }
  }

  async onForgotPassword(): Promise<void> {
    try {
      this.loader = true;

      let userName = this.forgotPasswordForm.value.emailF;

      await this.authService.forgotPassword(userName);

      this.showResetPasswordContent = false;
      this.changeMode(AuthPageMode.ResetPassword);
      //this.clearValues();
    } catch (error) {
      await this.authService.unautorized();
    } finally {
      this.loader = false;
    }
  }

  clearValues(): void {
    setTimeout(() => {
      this.passwordResetForm.reset();
    }, 100);
  }

  onResetPassword(): void {
    this.loader = true;

    let code = this.passwordResetForm.value.codeR;
    let userName = this.forgotPasswordForm.value.emailF;
    let newPasswordR = this.passwordResetForm.value.newPasswordR;
    let againPasswordR = this.passwordResetForm.value.againPasswordR;

    if (this.showCompletePassword) {
      this.authService
        .completeNewPassword(
          this.email.value,
          this.password.value,
          newPasswordR,
        )
        .then((res) => {
          if (res) {
            this.toastService.success(
              this.translate.instant('passwordChanged'),
            );
            this.changeMode(AuthPageMode.Login);
            this.showCompletePassword = false;
            location.reload();
            // this.router.navigate(['dashboard']).then(() => {
            //   console.log('auth: ', res);
            //   this.authService.setValidationResult = ValidationStatus.Valid;
            // });
          } else {
            this.toastService.error(
              this.translate.instant('failedResetPassword'),
            );
            this.showErrorResetPassword = true;
          }
        });
    } else {
      this.authService
        .forgotPasswordSubmit(userName, code, newPasswordR)
        .then((res) => {
          this.loader = false;
          if (res == 'SUCCESS') {
            this.toastService.success(
              this.translate.instant('passwordChanged'),
            );
            this.changeMode(AuthPageMode.Login);
          } else {
            this.toastService.error(
              this.translate.instant('failedResetPassword'),
            );
            this.showErrorResetPassword = true;
          }
        })
        .catch((err) => {
          console.log(err);
          this.showErrorResetPassword = true;
          this.toastService.error(
            this.translate.instant('failedResetPassword'),
          );
        });
    }
  }

  changeMode(mode: AuthPageMode): void {
    this.passwordVisible = true;
    this.authPageMode = mode;
    this.passwordResetForm.reset();
    this.showErrorResetPassword = false;
    this.resetValidations();
  }

  resetValidations(): void {
    this.isPasswordHasNumber =
      this.isPasswordHasSpecialCharacter =
      this.isPasswordLawerCase =
      this.isPasswordUpperCase =
      this.isPasswordUpTo8Characters =
      this.isMatchPasswords =
        false;
  }

  checkValidations(): void {
    this.isPasswordLawerCase = /[a-z]/.test(this.newPasswordR.value);
    this.isPasswordUpperCase = /[A-Z]/.test(this.newPasswordR.value);
    this.isPasswordHasNumber = /[0-9]/.test(this.newPasswordR.value);
    this.isPasswordHasSpecialCharacter =
      /[-+=!$%^&*()_|~`{}\[\]:\/;<>?,.@#'"]/.test(this.newPasswordR.value);
    this.isPasswordUpTo8Characters = this.newPasswordR.value?.length > 7;
    this.isMatchPasswords =
      this.againPasswordR.value === this.newPasswordR.value;
  }

  resetPasswordResultValidation(): boolean {
    return (
      !this.isMatchPasswords ||
      !this.passwordResetForm.valid ||
      !this.isPasswordHasNumber ||
      !this.isPasswordHasSpecialCharacter ||
      !this.isPasswordLawerCase ||
      !this.isPasswordUpperCase ||
      !this.isPasswordUpTo8Characters
    );
  }

  onChangeVisibility(el: HTMLInputElement): void {
    this.passwordVisible = !this.passwordVisible;

    if (this.passwordVisible) {
      el.type = 'password';
    } else {
      el.type = 'text';
    }
  }

  /**
   * Initial upcoming events and worker service
   *
   */
  async initialEvents(companyId: number): Promise<void> {
    try {
      await this.eventService.loadAllUpcomingEvents(companyId);
      this.eventWorkerService.resetWorker();
    } catch (error) {
      console.error(error);
    }
  }

  // keyPress(event: any, mode: string): void {
  //   if (mode === 'SignIn') {
  //     if (event.key === 'Enter') {
  //       if (this.loginForm.valid) {
  //         this.onLogin();
  //       }
  //     }
  //   }
  // }
}
